
import React from 'react';
import { useTranslation } from 'react-i18next';
import { SEO } from './SEO';
import {
  CheckCircle as CheckCircleIcon,
  Lightbulb as LightbulbIcon,
  Translate as TranslateIcon,
  School as SchoolIcon,
  Business as BusinessIcon,
  WhatsApp as WhatsAppIcon,
  Groups,
  Language,
  Verified,
  Speed,
  TrendingUp,
  EmojiEvents,
  MenuBook,
  Science
} from '@mui/icons-material';

const SarvalokForCommunities: React.FC = () => {
  const { t } = useTranslation(['org', 'seo']);
  
  return (
    <div className="bg-[#FBF7EF] text-[#0B1F41]">
      <SEO
        title={t('seo:org.title', { defaultValue: 'Enterprise Solutions | Sarvalok AI for Organizations' })}
        description={t('seo:org.description', { defaultValue: "Transform your organization with authentic cultural intelligence. Leverage Sarvalok's AI platform for research, content creation, and cultural education initiatives." })}
        url="/for-organisation"
      />
      {/* Community Header */}
      <div className="relative overflow-hidden bg-[linear-gradient(180deg,#FBF7EF_0%,#F3F7FB_60%,#FFFFFF_100%)] dark:bg-gradient-to-br dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 px-8 lg:px-20 py-16">
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-0">
          <div className="absolute -top-24 -right-24 z-0 h-80 w-80 rounded-full bg-[#D9E8F6] opacity-[0.35] blur-3xl dark:bg-sky-400/10" />
          <div className="absolute -bottom-28 -left-28 z-0 h-96 w-96 rounded-full bg-[#D9E8F6] opacity-[0.35] blur-3xl dark:bg-sky-400/10" />
          <img
            src="/decor/capsule3.png"
            alt=""
            className="absolute top-10 -right-8 z-10 hidden md:block h-auto w-auto max-w-[320px] opacity-90 drop-shadow-2xl"
          />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#D9E8F6] rounded-full mb-6">
            <Groups className="w-8 h-8 text-[#005EA2]" />
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold text-[#1A4480] dark:text-white mb-6 leading-tight">
            {t('org.header_title_line1')}
            <span className="block mt-2">
              <span className="inline-block text-[#D97706]">
                {t('org.header_title_line2')}
              </span>
            </span>
          </h1>
          <p className="text-xl text-slate-700 dark:text-gray-300 leading-relaxed max-w-3xl mx-auto">
            {t('org.header_description')}
          </p>
        </div>
      </div>

      <div className="px-8 lg:px-20 py-16">
        <div className="text-center mb-16">
          <h3 className="text-3xl font-bold text-gray-800 mb-4">{t('org.benefits_section_title')}</h3>
          <div className="w-24 h-1 bg-gradient-to-r from-[#005EA2] to-[#1A4480] mx-auto"></div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow duration-300 border border-[#E2E8F0]">
            <div className="flex items-center mb-6">
              <div className="bg-gradient-to-r from-blue-700 to-sky-500 p-3 rounded-lg">
                <TrendingUp className="text-white text-2xl" />
              </div>
              <h4 className="text-xl font-semibold ml-4 text-gray-800">{t('org.value_added_membership_title')}</h4>
            </div>
            <p className="text-gray-600 leading-relaxed">
              {t('org.value_added_membership_description')}
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow duration-300 border border-[#E2E8F0]">
            <div className="flex items-center mb-6">
              <div className="bg-gradient-to-r from-blue-400 to-indigo-500 p-3 rounded-lg">
                <Verified className="text-white text-2xl" />
              </div>
              <h4 className="text-xl font-semibold ml-4 text-gray-800">{t('org.reduce_middlemen_title')}</h4>
            </div>
            <p className="text-gray-600 leading-relaxed">
              {t('org.reduce_middlemen_description')}
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow duration-300 border border-[#E2E8F0]">
            <div className="flex items-center mb-6">
              <div className="bg-gradient-to-r from-teal-400 to-cyan-500 p-3 rounded-lg">
                <Language className="text-white text-2xl" />
              </div>
              <h4 className="text-xl font-semibold ml-4 text-gray-800">{t('org.language_support_title')}</h4>
            </div>
            <p className="text-gray-600 leading-relaxed">
              {t('org.language_support_description')}
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow duration-300 border border-[#E2E8F0]">
            <div className="flex items-center mb-6">
              <div className="bg-gradient-to-r from-green-400 to-blue-500 p-3 rounded-lg">
                 <MenuBook className="text-white text-2xl" />
              </div>
              <h4 className="text-xl font-semibold ml-4 text-gray-800">{t('org.reliable_content_title')}</h4>
            </div>
            <p className="text-gray-600 leading-relaxed">
              {t('org.reliable_content_description')}
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow duration-300 border border-[#E2E8F0]">
            <div className="flex items-center mb-6">
              <div className="bg-gradient-to-r from-orange-400 to-red-500 p-3 rounded-lg">
                <Speed className="text-white text-2xl" />
              </div>
              <h4 className="text-xl font-semibold ml-4 text-gray-800">{t('org.seamless_integration_title')}</h4>
            </div>
            <p className="text-gray-600 leading-relaxed">
              {t('org.seamless_integration_description')}
            </p>
          </div>
        </div>

        {/* Enrich Your Organization Section */}
        <div className="text-center mb-16">
          <h3 className="text-3xl font-bold text-gray-800 mb-4">{t('org.enrich_section_title')}</h3>
          <div className="w-24 h-1 bg-gradient-to-r from-[#005EA2] to-[#1A4480] mx-auto"></div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow duration-300 border border-[#E2E8F0]">
            <div className="flex items-center mb-6">
              <div className="bg-gradient-to-r from-yellow-400 to-orange-500 p-3 rounded-lg">
                <SchoolIcon className="text-white text-2xl" />
              </div>
              <h4 className="text-xl font-semibold ml-4 text-gray-800">{t('org.resource_members_title')}</h4>
            </div>
            <p className="text-gray-600 leading-relaxed">
              {t('org.resource_members_description')}
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow duration-300 border border-[#E2E8F0]">
            <div className="flex items-center mb-6">
              <div className="bg-gradient-to-r from-amber-500 to-orange-600 p-3 rounded-lg">
                <BusinessIcon className="text-white text-2xl" />
              </div>
              <h4 className="text-xl font-semibold ml-4 text-gray-800">{t('org.bundle_courses_title')}</h4>
            </div>
            <p className="text-gray-600 leading-relaxed">
              {t('org.bundle_courses_description')}
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow duration-300 border border-[#E2E8F0]">
            <div className="flex items-center mb-6">
              <div className="bg-gradient-to-r from-cyan-400 to-blue-500 p-3 rounded-lg">
                <LightbulbIcon className="text-white text-2xl" />
              </div>
              <h4 className="text-xl font-semibold ml-4 text-gray-800">{t('org.content_creation_title')}</h4>
            </div>
            <p className="text-gray-600 leading-relaxed">
              {t('org.content_creation_description')}
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow duration-300 border border-[#E2E8F0]">
            <div className="flex items-center mb-6">
              <div className="bg-gradient-to-r from-emerald-400 to-teal-500 p-3 rounded-lg">
                <Groups className="text-white text-2xl" />
              </div>
              <h4 className="text-xl font-semibold ml-4 text-gray-800">{t('org.training_development_title')}</h4>
            </div>
            <p className="text-gray-600 leading-relaxed">
              {t('org.training_development_description')}
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow duration-300 border border-[#E2E8F0]">
            <div className="flex items-center mb-6">
              <div className="bg-gradient-to-r from-teal-600 to-emerald-500 p-3 rounded-lg">
                 <EmojiEvents className="text-white text-2xl" />
              </div>
              <h4 className="text-xl font-semibold ml-4 text-gray-800">{t('org.employee_benefits_title')}</h4>
            </div>
            <p className="text-gray-600 leading-relaxed">
              {t('org.employee_benefits_description')}
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-lg p-8 hover:shadow-xl transition-shadow duration-300 border border-[#E2E8F0]">
            <div className="flex items-center mb-6">
              <div className="bg-gradient-to-r from-sky-600 to-cyan-500 p-3 rounded-lg">
                <Language className="text-white text-2xl" />
              </div>
              <h4 className="text-xl font-semibold ml-4 text-gray-800">{t('org.multilingual_engagement_title')}</h4>
            </div>
            <p className="text-gray-600 leading-relaxed">
              {t('org.multilingual_engagement_description')}
            </p>
          </div>
        </div>

        {/* Sarvalok Advantage Section */}
        <div className="bg-gradient-to-r from-slate-900 via-[#0B1F41] to-slate-900 rounded-xl p-8 text-white mb-16">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold mb-4">{t('org.advantage_section_title')}</h3>
            <div className="w-24 h-1 bg-gradient-to-r from-sky-400 to-cyan-300 mx-auto"></div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-2 gap-8 mb-8">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
              <div className="flex items-center mb-4">
                <Verified className="text-green-400 text-2xl mr-3" />
                <h4 className="text-xl font-semibold">{t('org.trusted_accuracy_title')}</h4>
              </div>
              <p className="text-gray-200 leading-relaxed">
                {t('org.trusted_accuracy_description')}
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
              <div className="flex items-center mb-4">
                <MenuBook className="text-blue-400 text-2xl mr-3" />
                <h4 className="text-xl font-semibold">{t('org.heritage_ai_title')}</h4>
              </div>
              <p className="text-gray-200 leading-relaxed">
                {t('org.heritage_ai_description')}
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
              <div className="flex items-center mb-4">
                <Science className="text-cyan-400 text-2xl mr-3" />
                <h4 className="text-xl font-semibold">{t('org.beyond_surface_title')}</h4>
              </div>
              <p className="text-gray-200 leading-relaxed">
                {t('org.beyond_surface_description')}
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6 border border-white/20">
              <div className="flex items-center mb-4">
                <Speed className="text-amber-300 text-2xl mr-3" />
                <h4 className="text-xl font-semibold">{t('org.save_time_title')}</h4>
              </div>
              <p className="text-gray-200 leading-relaxed">
                {t('org.save_time_description')}
              </p>
            </div>
          </div>

          <div className="text-center">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-8 border border-white/20">
              <h4 className="text-2xl font-bold mb-4">{t('org.authoritative_choice_title')}</h4>
              <p className="text-gray-200 leading-relaxed text-lg">
                {t('org.authoritative_choice_description')}
              </p>
            </div>
          </div>
        </div>

        {/* Partnership and Contact Section */}
        <div className="bg-gradient-to-br from-[#FBF7EF] to-[#EFF6FF] dark:from-gray-800 dark:to-gray-700 rounded-xl p-8">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-800 dark:text-white mb-4">{t('org.collaborate_title')}</h3>
            <div className="w-24 h-1 bg-gradient-to-r from-[#005EA2] to-[#1A4480] mx-auto mb-6"></div>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              {t('org.collaborate_description')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-md">
              <h4 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">{t('org.contact_partnerships_title')}</h4>
              <div className="space-y-3">
                <p><span className="font-semibold">{t('org.contact_email_label')}:</span> <a href="mailto:partnerships@sarvalok.com" className="text-[#005EA2] underline">partnerships@sarvalok.com</a></p>
                <p><span className="font-semibold">{t('org.contact_support_label')}:</span> <a href="mailto:support@sarvalok.com" className="text-[#005EA2] underline">support@sarvalok.com</a></p>
                <p><span className="font-semibold">{t('org.contact_phone_label')}:</span> +91 8125831103</p>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-md">
              <h4 className="text-xl font-semibold text-gray-800 dark:text-white mb-4">{t('org.quick_connect_title')}</h4>
              <a href="https://wa.me/918125831103?text=Hello%2C%20I%20have%20a%20question%20about%20Sarvalok.%20Can%20you%20please%20help%20me%3F" target="_blank" rel="noopener noreferrer" className="inline-flex items-center bg-green-500 text-white px-6 py-3 rounded-lg hover:bg-green-600 transition-colors duration-300">
                <WhatsAppIcon className="mr-2" />
                {t('org.whatsapp_button_text')}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SarvalokForCommunities;
